// HelpButton — drag-bar onboarding-bubbla.
//
// Default-position: mitten-vänster (vertikalt centrerad, 16 px in från
// vänsterkant). Hold-and-drag flyttar bubblan. Position clampas till
// viewport-bounds och persistas i sessionStorage (resetas vid reload).
//
// Visibility:
//   - dold om tour är aktiv (overlay har egna kontroller — kommentar i
//     gamla versionen)
//   - dold om wizard är öppen (per användarspec: "den skall finnas först
//     när wizzarden är helt stängd")
//
// Drag-mekanik: pointerdown → spara offset → pointermove följer fingret
// → pointerup commit:ar position. Touch + mouse via Pointer Events.

import { useEffect, useRef, useState, useCallback } from 'react';
import { HelpCircle } from 'lucide-react';
import { useOnboarding } from './useOnboarding';

const STORAGE_KEY = 'onboarding-bubble-pos';
const BUTTON_SIZE = 48;     // 12 × 4 px (w-12 h-12)
const VIEWPORT_MARGIN = 8;  // min-avstånd till viewport-kanten
// MobileTopBar = h-12 (48 px) + safe-area-inset-top. 60 px ger litet
// extra-buffer så bubblan aldrig hamnar UNDER topbar:n. Desktop TopBar
// är h-20 (80 px) — också OK eftersom bubblan ju är drag-bar och user
// kan flytta den om den hamnar i vägen.
const TOP_RESERVED = 60;
const CLICK_VS_DRAG_THRESHOLD = 4;

interface StoredPosition {
  x: number;
  y: number;
}

function defaultPosition(): StoredPosition {
  if (typeof window === 'undefined') return { x: 16, y: 300 };
  return {
    x: 16,
    y: Math.max(TOP_RESERVED, window.innerHeight / 2 - BUTTON_SIZE / 2),
  };
}

function loadStored(): StoredPosition | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
      return parsed;
    }
  } catch { /* ignore */ }
  return null;
}

function clampToViewport(pos: StoredPosition): StoredPosition {
  if (typeof window === 'undefined') return pos;
  const maxX = window.innerWidth - BUTTON_SIZE - VIEWPORT_MARGIN;
  const maxY = window.innerHeight - BUTTON_SIZE - VIEWPORT_MARGIN;
  return {
    x: Math.max(VIEWPORT_MARGIN, Math.min(maxX, pos.x)),
    // Min-Y = TOP_RESERVED för att hålla bubblan utanför TopBar-zonen
    // (mobile h-12 + safe-area). User kan inte dra in den under topbar:n.
    y: Math.max(TOP_RESERVED, Math.min(maxY, pos.y)),
  };
}

export function HelpButton() {
  const ctx = useOnboarding();
  const [pos, setPos] = useState<StoredPosition>(() => loadStored() ?? defaultPosition());
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef<{ offsetX: number; offsetY: number; moved: boolean } | null>(null);

  // Re-clamp vid resize så bubblan inte hamnar utanför viewporten när
  // user roterar enheten.
  useEffect(() => {
    const onResize = () => setPos((p) => clampToViewport(p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const rect = e.currentTarget.getBoundingClientRect();
    dragStateRef.current = {
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      moved: false,
    };
    setIsDragging(true);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragStateRef.current;
    if (!drag) return;
    const next = clampToViewport({
      x: e.clientX - drag.offsetX,
      y: e.clientY - drag.offsetY,
    });
    if (!drag.moved) {
      const dx = Math.abs(next.x - pos.x);
      const dy = Math.abs(next.y - pos.y);
      if (dx + dy > CLICK_VS_DRAG_THRESHOLD) drag.moved = true;
    }
    setPos(next);
  }, [pos.x, pos.y]);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragStateRef.current;
    dragStateRef.current = null;
    setIsDragging(false);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }

    if (drag?.moved) {
      try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // No real movement → treat as a tap: open the picker.
    ctx.setPickerVisible(true);
  }, [pos, ctx]);

  // Synlighets-villkor: dolda om tour aktiv eller wizard öppen.
  if (ctx.isActive) return null;
  if (ctx.wizardOpen) return null;

  return (
    <button
      type="button"
      aria-label={ctx.lang === 'sv' ? 'Onboarding-hjälp' : 'Onboarding help'}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => { dragStateRef.current = null; setIsDragging(false); }}
      className="fixed z-40 flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:opacity-90 transition-opacity touch-none select-none"
      style={{
        left: pos.x,
        top: pos.y,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
    >
      <HelpCircle className="w-6 h-6 pointer-events-none" />
    </button>
  );
}
